import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import withKycRequired from "@rentiohq/shared-frontend/dist/hoc/withKycRequired";
import logger from "@rentiohq/shared-frontend/dist/logger";
import * as paymentRequestApi from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.api";
import * as paymentRequestHooks from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.hooks";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import { EPaymentMethod } from "@rentiohq/shared-frontend/dist/types/payment.types";
import {
  EPaymentRequestFeature,
  EPaymentRequestStatus,
  IPaymentRequest,
} from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  EUIPaymentMethod,
  getAvailableOncePaymentMethods,
  getAvailableRecurringPaymentMethods,
} from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import { htmlTextAsReactText } from "@rentiohq/shared-frontend/dist/utils/text/text.utils";
import { ASSETS } from "@rentiohq/web-shared-next/dist/assets";
import {
  Box,
  Button,
  MainCard,
  Typography,
  Divider,
  Stack,
  RadioButtonsGroup,
  SpinningLoader,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { valuesForPaymentRequest } from "../../Payments.utils";
import { PaymentMethodOption } from "../PaymentMethodOption";
import { PaymentMethodSelectStyles } from "./PaymentMethodSelect.styles";

const PaymentMethodSelectBase = () => {
  const sx = PaymentMethodSelectStyles();
  const params = useParams();
  const navigate = useNavigate();

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const [isPaying, setIsPaying] = React.useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<
    EUIPaymentMethod | undefined
  >();

  const {
    detail: paymentRequest,
    fetchError: fetchErrorPaymentRequest,
    isFetching: isFetchingPaymentRequest,
  } = paymentRequestHooks.useDetail({
    id: paymentRequestId,
    shouldRefetch: true,
  });

  const {
    detail: property,
    isFetching: isFetchingProperty,
    fetchError: fetchErrorProperty,
  } = propertyHooks.useDetail({
    id: paymentRequest?.requestPropertyId,
    shouldRefetch: false,
  });

  React.useEffect(() => {
    if (
      paymentRequest &&
      paymentRequest.status === EPaymentRequestStatus.Paid
    ) {
      enqueueSnackbar({
        message: getLocalizedText("payment.error.already_paid.title"),
        variant: "error",
      });
      navigate("/payments");
    }
  }, [paymentRequest]);

  React.useEffect(() => {
    const fetchError = fetchErrorPaymentRequest || fetchErrorProperty;
    if (fetchError?.message) {
      enqueueSnackbar({
        message: fetchError?.message,
        variant: "error",
      });
    }
  }, [fetchErrorPaymentRequest, fetchErrorProperty]);

  const recurringPaymentMethods = paymentRequest
    ? getAvailableRecurringPaymentMethods(paymentRequest)
    : [
        EUIPaymentMethod.RecurringBankWire,
        EUIPaymentMethod.RecurringDirectDebit,
        EUIPaymentMethod.RecurringIbanisation,
      ];

  const oncePaymentMethods = paymentRequest
    ? getAvailableOncePaymentMethods(paymentRequest)
    : [
        EUIPaymentMethod.OnceBancontact,
        EUIPaymentMethod.OnceBankWire,
        EUIPaymentMethod.OnceIbanisation,
        EUIPaymentMethod.OnceIdeal,
        EUIPaymentMethod.OncePayconiq,
      ];

  const callbackUrl = paymentRequestId
    ? `${window.origin}/payment/${paymentRequestId}/status`
    : undefined;

  const handlePressSelect = () => {
    if (!selectedPaymentMethod) {
      enqueueSnackbar({
        message: getLocalizedText(
          "payment.online.select_method.cta_info.select",
        ),
        variant: "info",
      });
      return;
    }

    if (!paymentRequestId || !paymentRequest) {
      return;
    }

    const paymentRequestAmount = paymentRequest?.amount || 0;

    switch (selectedPaymentMethod) {
      case EUIPaymentMethod.RecurringDirectDebit:
        navigate(
          `/payment/${paymentRequestId}/payment-method/mangopay?type=recurring`,
        );
        break;

      case EUIPaymentMethod.OnceDirectDebit:
        navigate(
          `/payment/${paymentRequestId}/payment-method/mangopay?type=once`,
        );
        break;

      case EUIPaymentMethod.RecurringBankWire:
      case EUIPaymentMethod.OnceBankWire:
        navigate(`/payment/${paymentRequestId}/payment-method/bank-wire`);
        break;

      case EUIPaymentMethod.RecurringIbanisation:
      case EUIPaymentMethod.OnceIbanisation:
        navigate(`/payment/${paymentRequestId}/payment-method/ibanisation`);
        break;

      case EUIPaymentMethod.OnceBancontact:
        if (paymentRequestAmount > CONFIG.PAYMENT_LIMIT_AMOUNT_NOTIFICATION) {
          navigate(
            `/payment/${paymentRequestId}/payment-method/${EPaymentMethod.Bancontact}/payment-limit`,
          );

          break;
        }

        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Bancontact,
          paymentRequest,
        });

        break;

      case EUIPaymentMethod.OncePayconiq:
        if (paymentRequestAmount > CONFIG.PAYMENT_LIMIT_AMOUNT_NOTIFICATION) {
          navigate(
            `/payment/${paymentRequestId}/payment-method/${EPaymentMethod.Payconiq}/payment-limit`,
          );

          break;
        }

        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Payconiq,
          paymentRequest,
        });

        break;

      case EUIPaymentMethod.OnceIdeal:
        if (paymentRequestAmount > CONFIG.PAYMENT_LIMIT_AMOUNT_NOTIFICATION) {
          navigate(
            `/payment/${paymentRequestId}/payment-method/${EPaymentMethod.Ideal}/payment-limit`,
          );
          break;
        }

        handlePressWebPayment({
          paymentMethod: EPaymentMethod.Ideal,
          paymentRequest,
        });
        break;

      default:
        break;
    }
  };

  const handleMollieFlow = async () => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { data } = await paymentRequestApi.getMollieUrl(
      paymentRequestId,
      callbackUrl,
    );

    const { redirectUrl } = data;

    window.open(redirectUrl, "_self");
  };

  const handlePaymentWithBanqup = async (paymentMethod: EPaymentMethod) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { data } = await paymentRequestApi.generateBanqupWebPayment(
      paymentRequestId,
      paymentMethod,
      callbackUrl,
    );

    const { redirectUrl } = data;

    if (!redirectUrl) {
      throw new Error("No redirectUrl or templateUrl found for web payment");
    }

    setTimeout(() => {
      window.open(redirectUrl, "_self");
    }, 20);
  };

  const handlePaymentWithMangoPay = async (paymentMethod: EPaymentMethod) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { data } = await paymentRequestApi.generateWebPayment(
      paymentRequestId,
      paymentMethod,
      callbackUrl,
    );

    const { redirectUrl } = data;

    if (!redirectUrl) {
      throw new Error("No redirectUrl or templateUrl found for web payment");
    }

    setTimeout(() => {
      window.open(redirectUrl, "_self");
    }, 20);
  };

  const handlePressWebPayment = async (params: {
    paymentMethod: EPaymentMethod;
    paymentRequest: IPaymentRequest;
  }) => {
    if (!paymentRequestId || !callbackUrl) {
      return;
    }

    const { paymentMethod, paymentRequest } = params;

    setIsPaying(true);

    try {
      // Legacy Mollie flow
      const isLegacyMollieFlow = false;
      if (isLegacyMollieFlow) {
        handleMollieFlow();
        return;
      }

      if (
        paymentRequest?.features.includes(
          EPaymentRequestFeature.BanqupRentDeposit,
        )
      ) {
        handlePaymentWithBanqup(paymentMethod);
        return;
      }

      handlePaymentWithMangoPay(paymentMethod);
    } catch (unknownError) {
      const error = unknownError as any;
      logger.logError({ error });

      enqueueSnackbar({
        message:
          error?.response?.data?.error?.message ||
          getLocalizedText("general.error"),
        variant: "error",
      });

      setIsPaying(false);
    }
  };

  const handleSelectPaymentMethod = (
    newSelectedPaymentMethod: EUIPaymentMethod,
  ) => {
    setSelectedPaymentMethod(newSelectedPaymentMethod);
  };

  const getIcon = (paymentMethod: EUIPaymentMethod) => {
    switch (paymentMethod) {
      case EUIPaymentMethod.RecurringDirectDebit:
      case EUIPaymentMethod.OnceDirectDebit:
        return { icon: ASSETS.sepa, iconHeight: 12 };

      // case EUIPaymentMethod.RecurringBankWire:
      // case EUIPaymentMethod.OnceBankWire:
      // case EUIPaymentMethod.RecurringIbanisation:
      // case EUIPaymentMethod.OnceIbanisation:
      //   return ASSETS.bankTransfer;

      case EUIPaymentMethod.OnceBancontact:
        return { icon: ASSETS.bancontact, iconHeight: 16 };

      case EUIPaymentMethod.OnceIdeal:
        return { icon: ASSETS.ideal, iconHeight: 16 };

      case EUIPaymentMethod.OncePayconiq:
        return { icon: ASSETS.payconiq, iconHeight: 16 };

      default:
        break;
    }
  };

  const renderPaymentMethods = (paymentMethods: EUIPaymentMethod[]) => {
    return paymentMethods.map(paymentMethod => {
      const icon = getIcon(paymentMethod);
      return {
        label: (
          <PaymentMethodOption
            label={getLocalizedText(
              `payment.online.select_method.cta.${paymentMethod}`,
            )}
            description={getLocalizedText(
              `payment.online.select_method.description.${paymentMethod}`,
            )}
            icon={icon?.icon}
            iconHeight={icon?.iconHeight}
          />
        ),
        value: paymentMethod,
      };
    });
  };

  const isLoading = isFetchingPaymentRequest || isFetchingProperty || isPaying;

  return (
    <MainCard content={false}>
      <Box py={2} px={3}>
        <Typography variant="h4" sx={sx.content.heading}>
          {getLocalizedText("tenant.payments.select_payment_method.title")}
        </Typography>
        {paymentRequest && (
          <Typography variant="caption">
            {htmlTextAsReactText(
              getLocalizedText(
                "tenant.payments.select_payment_method.subtitle",
                valuesForPaymentRequest(paymentRequest, property),
              ),
            )}
          </Typography>
        )}
      </Box>
      <Divider />
      <Box p={3}>
        {isLoading ? (
          <SpinningLoader />
        ) : (
          <>
            <Stack direction="column" gap={1.5}>
              {recurringPaymentMethods?.length > 0 && (
                <RadioButtonsGroup
                  id="continuous"
                  label={getLocalizedText(
                    "payment.online.select_method.header.recurring",
                  )}
                  value={selectedPaymentMethod}
                  options={renderPaymentMethods(recurringPaymentMethods)}
                  onChange={handleSelectPaymentMethod}
                />
              )}
              {oncePaymentMethods?.length > 0 && (
                <RadioButtonsGroup
                  id="one-time"
                  label={getLocalizedText(
                    "payment.online.select_method.header.once",
                  )}
                  options={renderPaymentMethods(oncePaymentMethods)}
                  value={selectedPaymentMethod}
                  onChange={handleSelectPaymentMethod}
                />
              )}
            </Stack>
            <Stack direction="row" gap={1} mt={2.5}>
              <Button
                variant={EButtonVariants.Outlined}
                sx={sx.content.actionBtn}
                onClick={() => navigate(`/payment/${paymentRequestId}`)}
              >
                {getLocalizedText("system.previous")}
              </Button>
              <Button
                variant={EButtonVariants.Contained}
                sx={sx.content.actionBtn}
                onClick={handlePressSelect}
                disabled={!selectedPaymentMethod}
              >
                {getLocalizedText("system.next")}
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </MainCard>
  );
};

export const PaymentMethodSelect = withKycRequired(PaymentMethodSelectBase);
